/**
 * Azure Resource Service
 *
 * Provides APIs to fetch Azure resources for connection configuration:
 * - Subscriptions
 * - Cognitive Service accounts (Azure OpenAI, AI Services)
 * - AI Foundry projects
 * - Model deployments
 * - API Management instances
 *
 * Based on LogicAppsUX patterns from cognitiveService.ts
 */

// =============================================================================
// TYPES
// =============================================================================

export interface AzureSubscription {
  id: string;
  subscriptionId: string;
  displayName: string;
  state: string;
  tenantId: string;
}

export interface AzureTenant {
  id: string;
  tenantId: string;
  displayName: string;
  defaultDomain: string;
  tenantCategory: 'Home' | 'ProjectedBy' | 'ManagedBy';
}

export interface CognitiveAccount {
  id: string;
  name: string;
  type: string;
  kind: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  properties?: {
    endpoint?: string;
  };
}

export interface CognitiveProject {
  id: string;
  name: string;
  type: string;
  kind: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
  /** Parent account name (extracted from id) */
  accountName: string;
}

export interface ModelDeployment {
  id: string;
  name: string;
  properties: {
    model: {
      name: string;
      format: string;
      version: string;
    };
    provisioningState: string;
    raiPolicyName?: string;
  };
}

export interface AccountDetails {
  id: string;
  name: string;
  endpoint: string;
  location: string;
  kind: string;
}

export interface AccountKeys {
  key1: string;
  key2: string;
}

export interface ApimInstance {
  id: string;
  name: string;
  location: string;
  sku?: {
    name: string;
  };
}

export interface ApimApi {
  id: string;
  name: string;
  displayName?: string;
  path?: string;
}

export interface AzureResourceGroup {
  id: string;
  name: string;
  location: string;
  properties: {
    provisioningState: string;
  };
}

// =============================================================================
// CONSTANTS
// =============================================================================

import { getArmBaseUrl, isArmMockMode } from './armConfig';

const ARM_BASE_URL = getArmBaseUrl();
const RESOURCE_GRAPH_URL = `${ARM_BASE_URL}/providers/Microsoft.ResourceGraph/resources`;
// ARM API version for subscriptions endpoint - 2022-12-01 is stable and widely supported
const ARM_API_VERSION = '2022-12-01';
const TENANTS_API_VERSION = '2022-12-01';
const RESOURCE_GRAPH_API_VERSION = '2021-03-01';
// Cognitive Services API version - using same version as LogicAppsUX
const COGNITIVE_API_VERSION = '2023-10-01-preview';
const APIM_API_VERSION = '2022-08-01';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000; // 30 seconds

/**
 * Make an authenticated ARM API request with timeout
 */
async function armRequest<T>(
  accessToken: string,
  method: 'GET' | 'POST',
  url: string,
  body?: unknown
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (!isArmMockMode()) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const options: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      // Try to parse error as JSON for better message
      let errorMessage = `${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error?.message ?? errorJson.message ?? errorText;
      } catch {
        errorMessage = errorText || response.statusText;
      }
      throw new Error(`ARM API error: ${errorMessage}`);
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

interface PaginatedResponse<T> {
  value: T[];
  nextLink?: string;
}

/**
 * Fetch all pages from a paginated ARM API response
 */
async function armRequestPaginated<T>(
  accessToken: string,
  url: string
): Promise<T[]> {
  const results: T[] = [];
  let nextLink: string | null = url;

  while (nextLink) {
    const response: PaginatedResponse<T> = await armRequest<PaginatedResponse<T>>(
      accessToken,
      'GET',
      nextLink
    );
    results.push(...response.value);
    nextLink = response.nextLink ?? null;
  }

  return results;
}

/**
 * Execute a Resource Graph query and follow `$skipToken` pagination until
 * every page has been retrieved.
 *
 * Resource Graph returns at most `options.$top` rows per page (we use the
 * service max of 1000). To get the next page, callers must POST the same
 * query body again with `options.$skipToken` set to the opaque value
 * returned by the previous response. Resource Graph signals "no more pages"
 * by omitting `$skipToken` from the response.
 *
 * Exported so other services (e.g. applicationInsightsService) can reuse
 * the same Resource Graph plumbing. Consumers should mock this function
 * directly to unit-test response mapping.
 *
 * Throws if more than `MAX_PAGES` pages would be needed — better to surface
 * a clear error than silently return partial results, which would look like
 * "Azure dropped resources" to the user.
 */
export async function resourceGraphQuery<T>(
  accessToken: string,
  query: string,
  subscriptionIds: string[]
): Promise<T[]> {
  const url = `${RESOURCE_GRAPH_URL}?api-version=${RESOURCE_GRAPH_API_VERSION}`;

  const all: T[] = [];
  let skipToken: string | undefined;
  let pages = 0;
  const MAX_PAGES = 100;

  do {
    const response = await armRequest<{
      data: T[];
      $skipToken?: string;
    }>(accessToken, 'POST', url, {
      query,
      subscriptions: subscriptionIds,
      options: {
        resultFormat: 'ObjectArray',
        $top: 1000,
        ...(skipToken ? { $skipToken: skipToken } : {}),
      },
    });

    all.push(...response.data);
    skipToken = response.$skipToken;
    pages += 1;
  } while (skipToken && pages < MAX_PAGES);

  if (skipToken) {
    throw new Error(
      `Resource Graph query returned more than ${MAX_PAGES} pages of results; refusing to fetch further to avoid runaway requests. Narrow the query (e.g. scope to fewer subscriptions) and retry.`,
    );
  }

  return all;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get all subscriptions for the current user
 */
export async function getSubscriptions(accessToken: string): Promise<AzureSubscription[]> {
  const url = `${ARM_BASE_URL}/subscriptions?api-version=${ARM_API_VERSION}`;
  const subscriptions = await armRequestPaginated<AzureSubscription>(accessToken, url);

  // Filter to only enabled subscriptions
  return subscriptions
    .filter((sub) => sub.state === 'Enabled')
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Get all tenants accessible to the current user
 */
export async function getTenants(accessToken: string): Promise<AzureTenant[]> {
  const url = `${ARM_BASE_URL}/tenants?api-version=${TENANTS_API_VERSION}`;
  const tenants = await armRequestPaginated<AzureTenant>(accessToken, url);

  return tenants.sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Get all resource groups in a subscription
 */
export async function listResourceGroups(
  accessToken: string,
  subscriptionId: string
): Promise<AzureResourceGroup[]> {
  const url = `${ARM_BASE_URL}/subscriptions/${subscriptionId}/resourcegroups?api-version=2021-04-01`;
  const groups = await armRequestPaginated<AzureResourceGroup>(accessToken, url);
  return groups.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get Cognitive Service accounts (OpenAI, AIServices) in a subscription
 */
export async function getCognitiveAccounts(
  accessToken: string,
  subscriptionId: string
): Promise<CognitiveAccount[]> {
  const query = `
    Resources
    | where type == "microsoft.cognitiveservices/accounts"
    | where kind in ("OpenAI", "AIServices")
    | project id, name, type, kind, location, resourceGroup, subscriptionId, properties
    | order by name asc
  `;

  const results = await resourceGraphQuery<CognitiveAccount>(
    accessToken,
    query,
    [subscriptionId]
  );

  return results;
}

/** Base project result from Resource Graph (without accountName) */
interface ProjectGraphResult {
  id: string;
  name: string;
  type: string;
  kind: string;
  location: string;
  resourceGroup: string;
  subscriptionId: string;
}

/**
 * Get AI Foundry projects in a subscription
 */
export async function getCognitiveProjects(
  accessToken: string,
  subscriptionId: string
): Promise<CognitiveProject[]> {
  const query = `
    Resources
    | where type == "microsoft.cognitiveservices/accounts/projects"
    | project id, name, type, kind, location, resourceGroup, subscriptionId
    | order by name asc
  `;

  const results = await resourceGraphQuery<ProjectGraphResult>(
    accessToken,
    query,
    [subscriptionId]
  );

  // Extract account name from the resource ID or name
  return results.map((project): CognitiveProject => ({
    id: project.id,
    name: project.name,
    type: project.type,
    kind: project.kind,
    location: project.location,
    resourceGroup: project.resourceGroup,
    subscriptionId: project.subscriptionId,
    accountName: project.name.split('/')[0] ?? project.name,
  }));
}

/**
 * Get standalone Azure OpenAI accounts (v1 resources — not AI Foundry projects).
 * These are `microsoft.cognitiveservices/accounts` with kind `OpenAI`.
 */
export async function getOpenAIAccounts(
  accessToken: string,
  subscriptionId: string
): Promise<CognitiveAccount[]> {
  const query = `
    Resources
    | where type == "microsoft.cognitiveservices/accounts"
    | where kind == "OpenAI"
    | project id, name, type, kind, location, resourceGroup, subscriptionId, properties
    | order by name asc
  `;

  return resourceGraphQuery<CognitiveAccount>(
    accessToken,
    query,
    [subscriptionId]
  );
}

/**
 * Get account details including endpoint
 */
export async function getAccountDetails(
  accessToken: string,
  accountId: string
): Promise<AccountDetails> {
  const url = `${ARM_BASE_URL}${accountId}?api-version=${COGNITIVE_API_VERSION}`;

  const response = await armRequest<{
    id: string;
    name: string;
    location: string;
    kind: string;
    properties: {
      endpoint: string;
    };
  }>(accessToken, 'GET', url);

  return {
    id: response.id,
    name: response.name,
    endpoint: response.properties.endpoint,
    location: response.location,
    kind: response.kind,
  };
}

/**
 * Get account API keys
 */
export async function getAccountKeys(
  accessToken: string,
  accountId: string
): Promise<AccountKeys> {
  const url = `${ARM_BASE_URL}${accountId}/listKeys?api-version=${COGNITIVE_API_VERSION}`;

  const response = await armRequest<AccountKeys>(accessToken, 'POST', url);

  return response;
}

/**
 * Get model deployments for an account
 */
export async function getDeployments(
  accessToken: string,
  accountId: string
): Promise<ModelDeployment[]> {
  const url = `${ARM_BASE_URL}${accountId}/deployments?api-version=${COGNITIVE_API_VERSION}`;

  const deployments = await armRequestPaginated<ModelDeployment>(accessToken, url);

  // Filter to only succeeded deployments
  return deployments.filter(
    (d) => d.properties.provisioningState === 'Succeeded'
  );
}

/**
 * Get API Management instances in a subscription
 */
export async function getApimInstances(
  accessToken: string,
  subscriptionId: string
): Promise<ApimInstance[]> {
  const url = `${ARM_BASE_URL}/subscriptions/${subscriptionId}/providers/Microsoft.ApiManagement/service?api-version=${APIM_API_VERSION}`;

  const instances = await armRequestPaginated<ApimInstance>(accessToken, url);

  // Filter out Consumption tier (doesn't support AI gateway features)
  return instances
    .filter((i) => i.sku?.name?.toLowerCase() !== 'consumption')
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get APIs in an API Management instance
 */
export async function getApimApis(
  accessToken: string,
  apimInstanceId: string
): Promise<ApimApi[]> {
  const url = `${ARM_BASE_URL}${apimInstanceId}/apis?api-version=${APIM_API_VERSION}`;

  const apis = await armRequestPaginated<ApimApi>(accessToken, url);

  return apis.sort((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name));
}

// =============================================================================
// COMPOSITE FUNCTIONS
// =============================================================================

/**
 * Get account endpoint and key in one call
 * Used when user selects an account to auto-fill connection fields
 */
export async function getAccountCredentials(
  accessToken: string,
  accountId: string
): Promise<{ endpoint: string; key: string }> {
  const [details, keys] = await Promise.all([
    getAccountDetails(accessToken, accountId),
    getAccountKeys(accessToken, accountId),
  ]);

  return {
    endpoint: details.endpoint,
    key: keys.key1,
  };
}

/**
 * Build endpoint URL for AI Foundry project
 */
export function buildProjectEndpoint(accountName: string, projectName: string): string {
  return `https://${accountName}.services.ai.azure.com/api/projects/${projectName}`;
}

/** Result of parsing an Azure resource ID */
interface ParsedResourceId {
  subscriptionId?: string;
  resourceGroup?: string;
  provider?: string;
  resourceType?: string;
  resourceName?: string;
}

/**
 * Parse resource ID to extract components
 */
export function parseResourceId(resourceId: string): ParsedResourceId {
  const parts = resourceId.split('/');
  const result: ParsedResourceId = {};

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]?.toLowerCase();
    const nextPart = parts[i + 1];
    if (key === 'subscriptions' && nextPart) {
      result.subscriptionId = nextPart;
    } else if (key === 'resourcegroups' && nextPart) {
      result.resourceGroup = nextPart;
    } else if (key === 'providers' && nextPart) {
      result.provider = nextPart;
      const typePart = parts[i + 2];
      if (typePart) {
        result.resourceType = typePart;
      }
      const namePart = parts[i + 3];
      if (namePart) {
        result.resourceName = namePart;
      }
    }
  }

  return result;
}
